import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import * as logger from "../utils/logger";
import { BaseCache, makeDeviceCache } from "../utils/storage/cache";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { nativeSpeak } from "../utils/native/nativeSpeak";
import { SyncType } from "../sync/sync.types";
import { getCurrentVehicle, setCurrentVehicle, getCurrentVehicleName } from "../utils/device";
import DriverChangeLog from "../model/DriverChangeLog";
import { getServerDate } from "../utils/storage/date";
import { SubscriptionsService } from "../services/base";
import HosProcess from "./hosProcess";
import Sync from "../sync/sync";
import Api from "./api";
import { DriveStore } from "../store/reducers";
import { WorkdayType } from "../store/data/types";
import idSerializer from "geotab-uuid";
import DutyStatusLog, { NODEVICEID } from "../model/DutyStatusLog";
import Alerts from "./alerts";
import { UserService } from "../utils/users";
import {DutyStatusLogService} from "../model/DutyStatusLogs";
import TranslationService from "./translation";
import { Device } from "../model/Device";
import type { THosStatus } from "hos-tools";

export type THosSessionStatus = "Login" | "Logoff";

export interface IVehicleStatusData {
    odometer: string;
    engineHours: string;
}

export default class VehicleService extends SubscriptionsService {
    private readonly api: Api;
    private readonly user: string | null;
    private readonly sync: Sync;
    private readonly hosProcess: HosProcess;
    private readonly alerts: Alerts;
    private readonly store: DriveStore;
    private readonly deviceCache: BaseCache<IDevice | Device>;
    private readonly alertedOutOfRangeVehicles: IIdDictionary<boolean>;
    private readonly translationService: TranslationService;

    constructor(
        api: Api,
        user: string | null,
        sync: Sync,
        hosProcess: HosProcess,
        alerts: Alerts,
        private readonly userService: UserService,
        store: DriveStore,
        private readonly dutyStatusLogService: DutyStatusLogService,
        translationService: TranslationService
    ) {
        super();
        this.api = api;
        this.user = user;
        this.sync = sync;
        this.hosProcess = hosProcess;
        this.alerts = alerts;
        this.store = store;
        this.deviceCache = makeDeviceCache(this.store);
        this.alertedOutOfRangeVehicles = {};
        this.translationService = translationService;
    }

    setDriverVehicle(device?: IIdEntity | null, driver?: DeepReadonly<IUser> | "UnknownDriverId", force?: boolean, coDriver?: IIdEntity | null, driverChangeTime?: Date): Promise {
        return new Promise((resolve, reject) => {
            let currentUser = this.userService.getCurrentUser();
            let currentDevice = device || getCurrentVehicle();
            let currentDriver = driver || currentUser;
            let unknownDeviceId = NODEVICEID;
            let unknownDriverId = "UnknownDriverId";
            let addDriverChange = () => {
                let driverChange = new DriverChangeLog({
                    device: { id: currentDevice?.id ?? unknownDeviceId },
                    driver: currentDriver === "UnknownDriverId" ? { id: currentDriver } : { id: currentDriver ? currentDriver.id : "UnknownDriverId" },
                    type: "Driver",
                    dateTime: (driverChangeTime ?? getServerDate()).toISOString()
                });
                if (this.sync) {
                    return this.sync.driverChanges.addItem(driverChange)
                        .then(() => {
                            force && this.sync.synchronize(false, null, [SyncType.DriverChange]);
                            resolve();
                        });
                } else {
                    // Sync should never be undefined here
                    logger.error(logger.Source.USER, `VehicleService: Sync is undefined when trying to add DriverChange: ${ JSON.stringify(driverChange) }`, this.user);
                }
                resolve();
                return;
            };

            // don't add a driverchange for NoDeviceId
            if (currentDevice && currentDevice.id === "NoDeviceId") {
                logger.event(logger.Event.ADDDRIVERCHANGE, "Current user is not using a vehicle", this.user);
                resolve();
                return;
            } else if (currentDriver && currentDevice && (currentDriver === "UnknownDriverId" || currentDriver.driverGroups || currentDriver.keys)) {
                if (currentDriver === "UnknownDriverId") {
                    // When we're trying to disassociate a driver only, look up last driver change and don't unassign unless the driver is the current one
                    return this.sync.driverChanges.getLastItem(currentDevice)
                    .then(lastDriverChange => {
                        let otherUser: IIdEntity | null = lastDriverChange && lastDriverChange.driver;
                        let currentUserId = currentUser && currentUser.id;

                        if (!otherUser || otherUser.id === "UnknownDriverId" || otherUser.id === currentUserId || otherUser.id === coDriver?.id) {
                            logger.event(logger.Event.ADDDRIVERCHANGE, `A DriverChange was created for 'UnknownDriverId' using '${ currentDevice?.id || unknownDeviceId }'.`, this.user);
                            return addDriverChange();
                        }
                        logger.event(logger.Event.ADDDRIVERCHANGE, `This device already logged in to by '${ otherUser.id || unknownDriverId }'. Won't create new DriverChange.`, this.user);
                        resolve();
                        return;
                    })
                    .catch((error) => {
                        let store = this.store.getState();
                        let user = this.userService.getCurrentUser();

                        if (user && store.data.workday[user.id] === WorkdayType.Started && currentDevice && currentDevice.id !== "NoDeviceId") {
                            // If starting workday & trying to unassign a previous device, just add the DriverChange for now
                            //TODO: Move this whole logic into the DriverChange sync code
                            logger.event(
                                logger.Event.ADDDRIVERCHANGE,
                                `Error retrieving previous driver change, but a DriverChange was still created for 'UnknownDriverId' using '${
                                    currentDevice?.id || unknownDeviceId
                                }'.`,
                                this.user
                            );
                            return addDriverChange();
                        }
                        logger.error(logger.Source.USER, `Could not remove all driver associations for vehicle '${ currentDevice?.id || unknownDeviceId }'`, this.user, error);
                        reject(error);
                        return;
                    });
                } else {
                    logger.event(logger.Event.ADDDRIVERCHANGE, `A DriverChange was created for '${ currentDriver.id }' using '${ currentDevice.id }'.`, this.user);
                    return addDriverChange();
                }
            } else if (!(currentDriver !== "UnknownDriverId" && currentDriver?.driverGroups)) {
                logger.event(logger.Event.ADDDRIVERCHANGE, "Current user is likely not a driver", this.user);
                resolve();
                return;
            }
            resolve();
            return;
        });
    }

    checkActiveVehicle(vehicle: IIdEntity | null, callback: (result: boolean) => void) {
        let device = vehicle || getCurrentVehicle();

        if (device) {
            this.api.call("Get", {
                typeName: "Device",
                search: {
                    id: device.id
                }
            }, checkDevices => {
                let checkDevice = checkDevices[0];
                if (!checkDevice) {
                    // For now, ignore missing devices.
                    // TODO: Change this to false when we handle the case where co-drivers don't have the same access to the main driver's vehicle
                    callback(true);
                    return;
                }
                callback((checkDevice.activeTo && new Date(checkDevice.activeTo).getTime() >= getServerDate().getTime()) || checkDevice === "NoDeviceId");
            }, err => {
                logger.error(logger.Source.SYNC, `Couldn't get device to check it's existence and/or non-archived status: ${ err.message }`, this.user, err);
                // If this is a network error, let it slide for now
                callback(true);
            });
        } else {
            callback(false);
        }
    }

    selectVehicle(userName: string, vehicle: any): Promise {
        if (this.user && vehicle && userName.toLowerCase() === this.user.toLowerCase()) {
            let oldVehicle = getCurrentVehicle();

            setCurrentVehicle({id: vehicle.id});

            // Clear out of range alerts for this vehicle
            delete this.alertedOutOfRangeVehicles[vehicle.id];

            // A new device shouldn't have old device's trailers
            return this.onVehicleSelected(vehicle)
                .then(() => this.deviceCache.getOrFetch(this.api, [vehicle]))
                .then(populatedVehicles => {
                    return this.safeSetDriverVehicle(userName, vehicle).then(() => populatedVehicles);
                })
                .then(populatedVehicles => {
                    dispatcher.trigger(DispatcherEvents.VehicleSelected, vehicle, oldVehicle);
                    return this.sync.synchronize().then(() => populatedVehicles);
                }).then(populatedVehicles => {
                    if (populatedVehicles && populatedVehicles[0] && populatedVehicles[0][0]) {
                        return Promise.resolve(populatedVehicles[0][0]);
                    } else {
                        return Promise.reject();
                    }
                });
        } else {
            return Promise.reject();
        }
    }

    getLastLoginLogoffLogForUser(overrideUser: DeepReadonly<IUser> | null) {
        return this.hosProcess.getLatestLoginLogForUser(overrideUser || this.userService.getCurrentUser());
    }

    isLoginLogoffLogValid(logType: THosSessionStatus, device: IIdEntity, user: DeepReadonly<IUser> | null): boolean {
        let latestLoginLog = this.getLastLoginLogoffLogForUser(user);
        return !latestLoginLog || !(latestLoginLog.status === logType && latestLoginLog.getDeviceId() === device.id);
    }

    addLoginLogoffLog(
        logType: THosSessionStatus,
        user: DeepReadonly<IUser> | null,
        device: IIdEntity | null,
        syncLater = false,
        customDateTime?: Date,
        returnOnly = false
    ): Promise<DutyStatusLog | undefined> {
        if (device?.id && this.isLoginLogoffLogValid(logType, device, user)) {
            return this.addDutyStatusLog(logType, user, device, syncLater, customDateTime, returnOnly);
        }
        return Promise.resolve();
    }

    createLoginLogoffLogs(user: DeepReadonly<IUser> | null, newDevice: IIdEntity | null, overrideDate?: Date, syncLater?: boolean) {
        let promises: Promise[] = [];
        let now = overrideDate || getServerDate();

        const lastLoginLogoffLog = this.getLastLoginLogoffLogForUser(user);
        const currentDevice = lastLoginLogoffLog?.status === "Login" ? lastLoginLogoffLog.device : null;

        // if switching from one device to another, we need to log off the current device
        if (currentDevice && newDevice?.id !== currentDevice.id) {
            promises.push(this.addLoginLogoffLog("Logoff", user, currentDevice, true, new Date(now.getTime() - 10)));
        }
        promises.push(this.addLoginLogoffLog("Login", user, newDevice, syncLater, now));

        return promises;
    }

    /**
     * First clear all existing subs, and then listen to new events
     */
    startSubscriptions(): void {
        this.stopSubscriptions();
        /* eslint-disable @typescript-eslint/unbound-method */
        dispatcher.on(DispatcherEvents.DriverSwitch, this.onSwitchDriver, this);
        dispatcher.on(DispatcherEvents.VehicleProximity, this.onVehicleProximity, this);
        dispatcher.on(DispatcherEvents.VehicleOutOfRange, this.onVehicleOutOfRange, this);
        /* eslint-enable @typescript-eslint/unbound-method */
    }

    /**
     * Clears all subs in the VehicleService context for those event names
     */
    stopSubscriptions(): void {
        /* eslint-disable @typescript-eslint/unbound-method */
        dispatcher.off(DispatcherEvents.DriverSwitch, this.onSwitchDriver, this);
        dispatcher.off(DispatcherEvents.VehicleProximity, this.onVehicleProximity, this);
        dispatcher.off(DispatcherEvents.VehicleOutOfRange, this.onVehicleOutOfRange, this);
        /* eslint-enable @typescript-eslint/unbound-method */
    }

    destroy() {
        super.destroy();
        Object.keys(this.alertedOutOfRangeVehicles).forEach(deviceId => {
            delete this.alertedOutOfRangeVehicles[deviceId];
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    safeSetDriverVehicle(userName: string, device?: any, force?: boolean, _previousDriver: IUser | null = null, isAlreadyAssigned?: boolean) {
        let switchToUser = this.userService.getUserByName(userName);
        return switchToUser ? (isAlreadyAssigned ? Promise.resolve() : this.setDriverVehicle(device || null, switchToUser, !!force)) : Promise.resolve();
    }

    hasAccessToDevice(currentDevice: IIdEntity, api?: Api): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const thisApi = api || this.api;

            thisApi.call("Get", {
                typeName: "Device",
                search: currentDevice
            }, device => {
                if (device.length === 0) {
                    resolve(false);
                } else {
                    resolve(true);
                }
            }, err => {
                if (err.data.type.indexOf("NotSupportedException") !== -1 || err.data.type.indexOf("InvalidPermissionsException") !== -1) {
                    resolve(false);
                } else {
                    reject(err);
                }
            });
        });
    }

    private addDutyStatusLog(
        logType: THosStatus,
        user: DeepReadonly<IUser> | null,
        device?: IIdEntity,
        syncLater = false,
        customDateTime?: Date,
        returnOnly = false
    ): Promise<DutyStatusLog | undefined> {
        if (user && user.id) {
            let newLog = new DutyStatusLog({
                id: idSerializer.get(),
                dateTime: (customDateTime || getServerDate()).toISOString(),
                status: logType,
                device: { id: device?.id || "NoDeviceId" },
                driver: { id: user.id },
                coDrivers: this.userService.getCoDrivers(user.name),
                origin: "Manual"
            });
            return returnOnly ? Promise.resolve(newLog) : this.sync.dutyStatusLogs.addItem(newLog, syncLater).then(() => newLog);
        }
        return Promise.resolve();
    }

    private onVehicleSelected(currentDevice: IIdEntity): Promise {
        let user = this.user ? this.userService.getUserByName(this.user) : null;
        return user ? Promise.all(this.createLoginLogoffLogs(user, currentDevice, undefined, true)) : Promise.resolve();
    }

    private onSwitchDriver(userName: string, isVehicleSelect: boolean, isRemoveActiveDriver: boolean, previousDriver: IUser | null, isAlreadyAssigned?: boolean) {
        // If "isVehicleSelect", we don't need to add a driver change because assets/select does it already
        // If "isRemoveActiveDriver", we should defer this event to the watcher in AppManager
        if (isVehicleSelect || isRemoveActiveDriver) {
            return;
        }
        this.safeSetDriverVehicle(userName, null, true, previousDriver, isAlreadyAssigned)
            .catch(error => logger.error(logger.Source.USER, `Error when set driver vehicle : '${ error.message }'`, undefined, error));
    }

    private onVehicleProximity(userName: string, device: any) {
        this.selectVehicle(userName, device)
            .then(deviceResult => {
                let message = `Proximity-based assignment of '${ userName }' to vehicle '${ (deviceResult.name || deviceResult.id) }'`;
                logger.log(logger.Source.USER, message, this.user);
                if (this.store.getState().data.debugMode) {
                    nativeSpeak(message);
                }
            })
            .catch(error => logger.error(logger.Source.USER, `Error onVehicleProximity : '${ error.message }'`, undefined, error));
    }

    private onVehicleOutOfRange(userName: string) {
        let logOutOfRangeChoice = (accepted: boolean, newStatus?: THosStatus) => {
            let newStatusString = newStatus ? `, New Status: ${ newStatus }` : "";
            let message = `Unassignment of '${ userName }' from current vehicle - Accepted: ${ accepted ? "Y" : "N" }${ newStatusString }`;
            logger.log(logger.Source.USER, message, this.user);
        };

        let currentDevice = getCurrentVehicle();
        if (currentDevice) {
            if (this.alertedOutOfRangeVehicles[currentDevice.id]) {
                return undefined;
            }
            this.alertedOutOfRangeVehicles[currentDevice.id] = true;
        }

        const vehicleName = getCurrentVehicleName(this.store);
        const displayName = vehicleName ? ` (${ vehicleName })` : "";

        const alertTitle = "Vehicle Out of Range";
        const messageTemplate = `You appear to be too far from the vehicle {0}. Would you like to be unassigned from it?`;
        const confirmBtnText = "Yes & go OFF duty";
        const cancelBtnText = "Ignore";

        const alert = (title: string, message: string, confirm: string, cancel: string) =>
             this.alerts.prompt(message, title, [
                {
                    id: "vehicle_out_of_range-no",
                    name: cancel,
                    type: "secondary",
                    action: () => {
                        logOutOfRangeChoice(false);
                    }
                },
                {
                    id: "vehicle_out_of_range-yes-off",
                    name: confirm,
                    action: () => {
                    let device = getCurrentVehicle();
                    let user = this.api.getUser();
                    let latestStatusLog = this.dutyStatusLogService.getLatestStatusLog();
                    let isSituationalDriving = latestStatusLog !== null ? latestStatusLog.isSituationalDrivingStatus() : false;
                    let serverDate = getServerDate();

                    /*
                                       Order of logs being generated
                    (serverDate - 20)    (serverDate - 10)    (serverDate)      (serverDate + 10)
                           |                   |                   |                 |
                        Logoff               Login           SituationalDriving     OFF
                       (from createLoginLogOffLogs)           (if applicable)
                   */
                    let logoffLoginLogsTime = new Date(serverDate.getTime() - 10);

                    Promise.all(this.createLoginLogoffLogs(user, { id: "NoDeviceId" }, logoffLoginLogsTime, true))
                        .then(() => {
                            const offLogDateTime = new Date(serverDate.getTime() + 10);
                            if (isSituationalDriving) {
                                const newLogs = this.dutyStatusLogService.getLogsToEndDrivingExemption(user, latestStatusLog, offLogDateTime, "Manual", "OFF");
                                return newLogs.length
                                    ? Promise.all(newLogs.map((newLog, index) => this.sync.dutyStatusLogs.addItem(newLog, index !== newLogs.length - 1)))
                                    : Promise.resolve();
                            }
                            return this.addDutyStatusLog("OFF", user, device || undefined, false, offLogDateTime);
                        })
                        .then(() => this.setDriverVehicle(device, "UnknownDriverId", true, this.userService.getDriversSeatUser()))
                        .then(() => {
                            setCurrentVehicle({ id: "NoDeviceId" });
                            dispatcher.trigger(DispatcherEvents.VehicleSelected);
                            logOutOfRangeChoice(true, "OFF");

                            // Reset this vehicle's out of range alert status in case it's re-selected
                            device && delete this.alertedOutOfRangeVehicles[device.id];
                        })
                        .catch(error => logger.error(logger.Source.USER, `Error when adding DutyStatusLog : '${ error.message }'`, undefined, error));
                    }
                }
            ], false);

        const translator = this.translationService.getTranslator();

        return translator.translate([alertTitle, messageTemplate, confirmBtnText, cancelBtnText])
            .then(translatedValues => alert(translatedValues[0], translatedValues[1].replace("{0}", displayName), translatedValues[2], translatedValues[3]));
    }
}